import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Card, TextButton } from '@amzn/storm-ui';
import useIntl from '@i18n/useIntl';
import CombineAccountsLayout from '@features/combine-accounts/components/CombineAccountsLayout';
import CombineAccountsActionButtons from '@features/combine-accounts/components/CombineAccountsActionButtons';
import {
  COMBINE_ACCOUNTS_LANDING_PATH,
  ucmAllCampaignsUrl,
} from 'src/shared/routes';
import {
  UPGRADE_ACCOUNTS_HEADER_KEY,
  UPGRADE_ACCOUNTS_CHOOSE_HEADING_KEY,
  UPGRADE_ACCOUNTS_CHOOSE_DESCRIPTION_KEY,
  UPGRADE_ACCOUNTS_SELECTED_COUNT_KEY,
  UPGRADE_ACCOUNTS_CONFIRM_HEADER_KEY,
  UPGRADE_ACCOUNTS_CONFIRM_BODY_KEY,
  UPGRADE_ACCOUNTS_CONFIRM_BULLET_CAMPAIGNS_KEY,
  UPGRADE_ACCOUNTS_CONFIRM_BULLET_USERS_KEY,
  UPGRADE_ACCOUNTS_CONFIRM_BULLET_FEATURES_KEY,
  UPGRADE_ACCOUNTS_CONFIRM_CHECKBOX_KEY,
  COMBINE_ACCOUNTS_REQUEST_ACCESS_AGREEMENT_LINK_KEY,
  AMAZON_ADS_AGREEMENT_PATH,
  UPGRADE_ACCOUNTS_CONFIRM_SUBMIT_KEY,
  UPGRADE_ACCOUNTS_CONFIRM_PENDING_HEADER_KEY,
  UPGRADE_ACCOUNTS_CONFIRM_PENDING_BODY_KEY,
  UPGRADE_ACCOUNTS_CONFIRM_ERROR_KEY,
  UPGRADE_ACCOUNTS_LOADING_KEY,
  COMBINE_ACCOUNTS_REQUEST_SUBMITTED_EMAIL_NOTE_KEY,
  COMBINE_ACCOUNTS_DONE_KEY,
  COMBINE_ACCOUNTS_CANCEL_KEY,
} from '@features/combine-accounts/constants';
import { CombineAccountType } from 'src/shared/services/combine-accounts/types';
import { AccountItem, FILTER_KEY_DSP } from '../combineChoose/types';
import ChooseAccountsStep from '../combineChoose/components/ChooseAccountsStep';
import ConfirmationModal from '../combineChoose/components/ConfirmationModal';
import useUpgradeProgress from './useUpgradeProgress';

/**
 * Marks a page-level submit failure for this lane, as opposed to the field-level error the
 * request-access tab raises against its own input. The shared error channel carries both, and only
 * the matching target is rendered here.
 */
const UPGRADE_ERROR_TARGET_ID = 'upgrade-error';

const UpgradeChoosePage: React.FC = () => {
  const navigate = useNavigate();
  const { intl } = useIntl();
  const [selectedItems, setSelectedItems] = useState<AccountItem[]>([]);
  const [errorAlert, setErrorAlert] = useState<{
    message: string;
    targetId: string;
  } | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const { phase, runUpgrade } = useUpgradeProgress();
  /*
    Covers both phases. The modal's spinner has to stay up for the polling window
    as well as the submit, because from the user's point of view it is one wait:
    the request being accepted is not the thing they are waiting for.
  */
  const isSubmitting = phase !== 'idle';
  const isConfirmInFlight = useRef(false);

  const handleSelectionChange = useCallback((items: AccountItem[]) => {
    setSelectedItems(items);
    setErrorAlert(null);
  }, []);

  const handleAddRequestAccessItem = useCallback((item: AccountItem) => {
    setSelectedItems((p) => [...p, item]);
    setErrorAlert(null);
  }, []);

  const hasPendingAccess = selectedItems.some((i) => i.isPendingAccess);

  const handleCancel = () => navigate(COMBINE_ACCOUNTS_LANDING_PATH);

  const handleSubmit = () => {
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    /*
      Synchronous guard, checked and set before the first await. isSubmitting is
      derived from the hook's phase, which only reaches the DOM on the next
      render, so a fast second click would otherwise create a second upgrade.
      This mattered less when the modal closed immediately on confirm, because
      the control unmounted; now that it stays open for the whole wait the guard
      is load-bearing.
    */
    if (isConfirmInFlight.current) return;
    isConfirmInFlight.current = true;

    const dspAccountIds = selectedItems
      .filter((item) => item.accountType === CombineAccountType.DSP)
      .map((item) => String(item.id));

    try {
      /*
        No target account is sent. An upgrade creates one new global Advertiser
        account per listed account, so unlike combine there is nothing to merge
        into, and the endpoint takes no sponsoredAdsAccountId.

        This waits for the upgrade rather than for the request: the endpoint
        answers 202 and the work continues afterwards, so navigating on that
        response told the user their account was upgraded while it was still
        being built. runUpgrade submits, then follows the accounts until they
        reach CREATED or its threshold passes.
      */
      const { upgraded, failed, pending } = await runUpgrade(dspAccountIds);

      setShowConfirmModal(false);
      setConfirmChecked(false);

      /*
        One upgraded account and nothing else outstanding is the only outcome
        that leaves this area, because it is the only one with a single
        unambiguous destination. Several successes, a refusal or anything still
        running all need a per-account report, which belongs on the landing page.

        The URL is undefined unless the status read returned both the entity and
        the account's own host, so an account whose link cannot be built safely
        falls back to the landing page rather than opening campaign manager on
        whichever account it defaults to.
      */
      const singleSuccess =
        upgraded.length === 1 && failed.length === 0 && pending.length === 0;
      const campaignsUrl = singleSuccess
        ? ucmAllCampaignsUrl(upgraded[0].entityId, upgraded[0].accountUrl)
        : undefined;

      if (campaignsUrl) {
        // A real navigation, not a router one: campaign manager is outside this
        // SPA's route table, and on another marketplace it is another origin.
        window.location.assign(campaignsUrl);
        return;
      }

      /*
        Everything else is handed to the landing page to report, including the
        accounts still running: this page stops watching at its threshold, and
        without the hand-over a slow upgrade left quietly with nothing said about
        it anywhere.

        The names travel with it because neither the upgrade response nor the
        status read returns one, and they are already on screen here. Only the
        accounts actually submitted are included, so the state stays proportional
        to the request rather than to whatever the user browsed.
      */
      const accountNames: Record<string, string> = {};
      selectedItems
        .filter((item) => dspAccountIds.includes(String(item.id)))
        .forEach((item) => {
          // Built by assignment rather than Object.fromEntries, which this
          // package's TS lib target predates.
          accountNames[String(item.id)] = String(item.label ?? '');
        });

      navigate(COMBINE_ACCOUNTS_LANDING_PATH, {
        state: {
          outstandingUpgrades: pending.map((dspAccountId) => ({
            dspAccountId,
          })),
          upgradedAccounts: upgraded,
          upgradeFailures: failed,
          accountNames,
        },
      });
    } catch (err) {
      setShowConfirmModal(false);
      setConfirmChecked(false);
      setErrorAlert({
        message: intl.getMessage(UPGRADE_ACCOUNTS_CONFIRM_ERROR_KEY),
        targetId: UPGRADE_ERROR_TARGET_ID,
      });
    } finally {
      isConfirmInFlight.current = false;
    }
  };

  return (
    <CombineAccountsLayout
      titleKey={UPGRADE_ACCOUNTS_HEADER_KEY}
      onClose={handleCancel}
      actionButtons={
        <CombineAccountsActionButtons
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          submitDisabled={selectedItems.length === 0 || isSubmitting}
        />
      }
    >
      <Card>
        {/*
          TODO: move this filtering server-side once AMACS exposes an
          account-type filter on QueryAccountHierarchies (in flight as of
          2026-08-20) and ARMS threads it through
          GET /combine/choose-accounts-context, which today accepts only query,
          paginationToken and maxResults.

          Filtering in the client is correct but not honest about totals: the
          BFF still returns every account type, so a fetched page of ten may
          render as two or three rows, "Load more accounts" advances in uneven
          steps, and the response's totalCount counts accounts this lane never
          shows. Filtering at the query keeps pagination and counts aligned.
        */}
        {/*
          The only renderer for this lane's submit failure. errorAlert is also
          handed to ChooseAccountsStep, but the sole component that displays it
          is RequestAccessTab, which this lane removes via hideRequestAccessTab,
          and which in any case only renders a targetId of
          'request-access-input'. Without this the modal simply closed on a
          failed upgrade and the user was told nothing.
        */}
        {errorAlert?.targetId === UPGRADE_ERROR_TARGET_ID && (
          <Alert
            variant="error"
            withCloseButton
            isOpen
            onClose={() => setErrorAlert(null)}
          >
            {errorAlert.message}
          </Alert>
        )}

        <ChooseAccountsStep
          restrictToAccountType={FILTER_KEY_DSP}
          selectedItems={selectedItems}
          onSelectionChange={handleSelectionChange}
          onAddRequestAccessItem={handleAddRequestAccessItem}
          errorAlert={errorAlert}
          onError={setErrorAlert}
          onClearError={() => setErrorAlert(null)}
          heading={intl.getMessage(UPGRADE_ACCOUNTS_CHOOSE_HEADING_KEY)}
          description={intl.getMessage(UPGRADE_ACCOUNTS_CHOOSE_DESCRIPTION_KEY)}
          labelsSelected={intl.formatMessage(
            UPGRADE_ACCOUNTS_SELECTED_COUNT_KEY,
            { count: selectedItems.length },
          )}
          allowMultipleSameType
          hideRequestAccessTab
          hideFilterBy
          allowAddAll
        />
      </Card>

      <ConfirmationModal
        isOpen={showConfirmModal}
        hasPendingAccess={hasPendingAccess}
        selectedItems={selectedItems}
        confirmChecked={confirmChecked}
        onConfirmCheckedChange={setConfirmChecked}
        onClose={() => {
          setShowConfirmModal(false);
          setConfirmChecked(false);
        }}
        onConfirm={handleConfirm}
        submitting={
          isSubmitting
            ? { label: intl.getMessage(UPGRADE_ACCOUNTS_LOADING_KEY) }
            : undefined
        }
        header={intl.getMessage(UPGRADE_ACCOUNTS_CONFIRM_HEADER_KEY)}
        bodyText={intl.getMessage(UPGRADE_ACCOUNTS_CONFIRM_BODY_KEY)}
        bullets={[
          intl.getMessage(UPGRADE_ACCOUNTS_CONFIRM_BULLET_CAMPAIGNS_KEY),
          intl.getMessage(UPGRADE_ACCOUNTS_CONFIRM_BULLET_USERS_KEY),
          intl.getMessage(UPGRADE_ACCOUNTS_CONFIRM_BULLET_FEATURES_KEY),
        ]}
        checkboxLabel={intl.formatMessage(
          UPGRADE_ACCOUNTS_CONFIRM_CHECKBOX_KEY,
          {
            // Mirrors the request-access tab so both click-throughs open the
            // same route with the same label. Until Panther returns the
            // reworded source string, locales still holding the previous
            // wording simply render it without a link rather than breaking:
            // an unused argument is ignored.
            agreement: (
              <TextButton
                onClick={() =>
                  window.open(
                    AMAZON_ADS_AGREEMENT_PATH,
                    '_blank',
                    'noopener,noreferrer',
                  )
                }
              >
                {intl.getMessage(
                  COMBINE_ACCOUNTS_REQUEST_ACCESS_AGREEMENT_LINK_KEY,
                )}
              </TextButton>
            ),
          },
        )}
        submitLabel={intl.getMessage(UPGRADE_ACCOUNTS_CONFIRM_SUBMIT_KEY)}
        cancelLabel={intl.getMessage(COMBINE_ACCOUNTS_CANCEL_KEY)}
        pendingHeader={intl.getMessage(
          UPGRADE_ACCOUNTS_CONFIRM_PENDING_HEADER_KEY,
        )}
        pendingBodyText={intl.getMessage(
          UPGRADE_ACCOUNTS_CONFIRM_PENDING_BODY_KEY,
        )}
        // Defensive wiring. The upgrade lane hides the Request access tab, so
        // no selected item can carry isPendingAccess and this branch is
        // currently unreachable here. The two keys below are the combine
        // lane's: they replace a single hardcoded string that previously
        // served both call sites, so rendering is unchanged. If upgrade ever
        // gains a pending path, give it its own copy rather than editing the
        // request-access strings.
        pendingEmailNote={intl.getMessage(
          COMBINE_ACCOUNTS_REQUEST_SUBMITTED_EMAIL_NOTE_KEY,
        )}
        doneLabel={intl.getMessage(COMBINE_ACCOUNTS_DONE_KEY)}
      />
    </CombineAccountsLayout>
  );
};

export default UpgradeChoosePage;
